import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash.debounce';
import DataTable from 'react-data-table-component';
import { Button, Card, CardBody, Col, Container, Input, Label, Row } from 'reactstrap';
import { Trash } from 'react-feather';
import Breadcrumbs from '../CommonElements/Breadcrumbs';
import ConfirmationModal from '../Components/ConfirmationModal';
import {
  deleteWebsiteLeads,
  getWebsiteLeads,
} from '../ReduxToolkit/Reducers/websiteLeads';

function WebsiteLeads() {
  const dispatch = useDispatch();
  const { websiteLeads, totalWebsiteLeads, isLoading } = useSelector(
    (state) => state.websiteLeads,
  );

  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchText, setSearchText] = useState('');
  const [tableData, setTableData] = useState([]);
  const [selectedRowsData, setSelectedRowsData] = useState(null);
  const [singleDeleteLead, setSingleDeleteLead] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [toggleCleared, setToggleCleared] = useState(false);

  useEffect(() => {
    if (page) {
      fetchLeads();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, itemsPerPage, searchText]);

  useEffect(() => {
    if (websiteLeads[page]) {
      setTableData(websiteLeads[page]);
    }
  }, [websiteLeads, page]);

  const fetchLeads = async (hideLoader = false) => {
    await dispatch(getWebsiteLeads(page, searchText, itemsPerPage, hideLoader));
  };

  const searchLeads = (inputValue) => {
    const query = inputValue?.trim();
    setPage(1);
    setSearchText(query);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSearch = useCallback(debounce(searchLeads, 700), []);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    debounceSearch(e.target.value);
  };

  const closeConfirmationModal = () => {
    setConfirmationModal(false);
    setSingleDeleteLead(null);
  };

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      const leadIds = singleDeleteLead
        ? [singleDeleteLead]
        : selectedRowsData?.selectedRows?.map((lead) => lead.id);

      const result = await dispatch(deleteWebsiteLeads(leadIds));

      if (result?.statusCode === 200) {
        setSelectedRowsData(null);
        setToggleCleared((prev) => !prev);
        // Deleting can empty the last page, so step back when that happens.
        const remaining = totalWebsiteLeads - leadIds.length;
        const lastPage = Math.max(1, Math.ceil(remaining / itemsPerPage));
        if (page > lastPage) {
          setPage(lastPage);
        } else {
          fetchLeads();
        }
      }
    } catch (e) {
      console.error('Delete error:', e);
    } finally {
      setDeleteLoading(false);
      closeConfirmationModal();
    }
  };

  const columns = [
    {
      name: 'Company Name',
      selector: (row) => row.company_name || '--',
      sortable: true,
      minWidth: '180px',
    },
    {
      name: 'Website',
      cell: (row) =>
        row.website ? (
          <a
            href={row.website.startsWith('http') ? row.website : `https://${row.website}`}
            target="_blank"
            rel="noreferrer"
          >
            {row.website}
          </a>
        ) : (
          '--'
        ),
      minWidth: '200px',
    },
    {
      name: 'Contact Name',
      selector: (row) => row.contact_name || '--',
      sortable: true,
      minWidth: '160px',
    },
    {
      name: 'Email',
      selector: (row) => row.email || '--',
      minWidth: '200px',
    },
    {
      name: 'Phone',
      selector: (row) => row.phone || '--',
      minWidth: '150px',
    },
    {
      name: 'Type',
      selector: (row) => row.type || '--',
      minWidth: '120px',
    },
    {
      name: 'Created At',
      cell: (row) =>
        row.created_at
          ? new Date(row.created_at).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })
          : '--',
      minWidth: '140px',
    },
    {
      name: 'Actions',
      cell: (row) => (
        <Button
          color="danger"
          size="sm"
          outline
          onClick={() => {
            setSingleDeleteLead(row.id);
            setConfirmationModal(true);
          }}
        >
          <Trash size={14} />
        </Button>
      ),
      center: true,
      width: '100px',
    },
  ];

  return (
    <div className="page-body">
      <Breadcrumbs pageTitle="Website Leads" parent="Home" title="Website Leads" />
      <Container fluid>
        <Card>
          <CardBody>
            <Row className="align-items-center justify-content-between mb-3">
              <Col xs="auto">
                {selectedRowsData?.selectedCount > 0 && (
                  <Button
                    outline
                    color="primary"
                    onClick={() => setConfirmationModal(true)}
                  >
                    <Trash size={16} /> Delete ({selectedRowsData.selectedCount})
                  </Button>
                )}
              </Col>
              <Col xs="auto" className="d-flex align-items-center gap-2">
                <Label className="mb-0">Search:</Label>
                <Input
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="Search by company, website, contact, email or phone"
                  style={{ minWidth: 320 }}
                />
              </Col>
            </Row>

            <DataTable
              striped
              pagination
              paginationServer
              data={tableData}
              columns={columns}
              progressPending={isLoading}
              paginationTotalRows={totalWebsiteLeads}
              paginationPerPage={itemsPerPage}
              paginationDefaultPage={page}
              customStyles={{ tableWrapper: { style: { minHeight: 500 } } }}
              selectableRows
              clearSelectedRows={toggleCleared}
              onSelectedRowsChange={setSelectedRowsData}
              onChangePage={(newPage) => setPage(newPage)}
              onChangeRowsPerPage={(count) => {
                setPage(1);
                setItemsPerPage(count);
              }}
            />
          </CardBody>
        </Card>
      </Container>

      <ConfirmationModal
        open={confirmationModal}
        onClose={closeConfirmationModal}
        onSubmit={handleDelete}
        isLoading={deleteLoading}
        title="Delete Website Lead"
        message={
          singleDeleteLead
            ? 'Are you sure you want to permanently delete this website lead? This action cannot be undone.'
            : `Are you sure you want to permanently delete ${selectedRowsData?.selectedCount || 0} website lead(s)? This action cannot be undone.`
        }
      />
    </div>
  );
}

export default WebsiteLeads;
